'use client';
import { cloneElement, isValidElement, type ReactElement } from 'react';
import { PublicVbc, type Action } from '@to/public-vbc';
import '@to/public-vbc/style.css';
import type { PublicVbcContent } from '@to/db/vendor';
import type { RenderedTrustBadge, CategoryItem, ServiceArea, SpecialOfferItem } from './vbc';

export interface VendorDetailVendor {
  content?: PublicVbcContent | null;
  companyName: string;
  profileTitle?: string | null;
  logo?: string | null;
  description?: string | null;
  // Contact
  contactName?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
  phone?: string | null;
  cellPhone?: string | null;
  email?: string | null;
  website?: string | null;
  // Credentials
  licenseNumber?: string | null;
  licenseType?: string | null;
  licenseExpiry?: string | null;
}

export interface VendorDetailPageProps {
  vendor: VendorDetailVendor;
  vendorId: string | number;

  // Structured related data
  trustBadges?: RenderedTrustBadge[];
  customBadges?: { name: string; imageUrl: string | null }[];
  categories?: CategoryItem[];
  serviceAreas?: ServiceArea[];
  offers?: SpecialOfferItem[];

  // Navigation
  backLink?: { href: string; label?: string };
  categoryBasePath?: string;

  /**
   * Full vCard 3.0 text the QR encodes. When set, the Contact Information
   * card renders a "Save to your contacts" QR beneath the contact details.
   * Apps build the string server-side via `buildVCardString` from
   * `@to/vendor`. Phones recognize the embedded vCard payload on scan and
   * prompt to save the contact natively — no browser detour.
   */
  vcardContent?: string | null;

  // Augmentation slots — apps pass custom React nodes
  slots?: {
    afterHero?: React.ReactNode;
    beforeContact?: React.ReactNode;
    contactActionsSlot?: React.ReactNode;
    afterAbout?: React.ReactNode;
    afterCredentials?: React.ReactNode;
    bottom?: React.ReactNode;
  };

  onAction?: (action: Action) => void;
  // Callbacks for interactive components (passed through to VBCContactBar)
  onTrackInteraction?: (type: string, vendorId: string | number) => void;
}


/** App data/action adapter. The sole public presentation source is @to/public-vbc. */
export function VendorDetailPage({vendor, vendorId, trustBadges=[], customBadges=[], categories=[], serviceAreas=[], offers=[], backLink, vcardContent, slots={}, onTrackInteraction, onAction}: VendorDetailPageProps) {
  return <PublicVbc vendor={{...vendor,cellPhone:vendor.content?.contact?.cell_phone_number??vendor.cellPhone, address: [vendor.address,vendor.city,vendor.state,vendor.zipCode].filter(Boolean).join(', '), addressLines:[vendor.address,[vendor.city,[vendor.state,vendor.zipCode].filter(Boolean).join(' ')].filter(Boolean).join(', ')].filter((v):v is string=>!!v)}}
    categories={categories.map(c=>c.name)} serviceAreas={serviceAreas.map(a=>[a.name,a.state].filter(Boolean).join(', '))}
    badges={[...customBadges, ...trustBadges.map(b=>({name:b.label}))]}
    offers={offers.map(o=>({id:String(o.id),title:o.title,description:o.description,image:o.imagePath}))}
    backLink={backLink} vcardContent={vcardContent}
    onAction={action=>{ if(onAction) onAction(action); else if(['phone_click','cell_phone_click','website_click','email_click'].includes(action.type)) onTrackInteraction?.(action.type,vendorId); }}
    renderInquiry={isValidElement(slots.contactActionsSlot) ? ({selectedOffer,onAction:emit,intent,offerTitle})=>cloneElement(slots.contactActionsSlot as ReactElement<{selectedOffer?:string|null;onBehavior?:(action:Action)=>void;intent?:'offer_activation';offerTitle?:string}>, {selectedOffer,onBehavior:emit,intent,offerTitle}) : undefined}
  />;
}
